import { useNavigate } from "react-router-dom";
import Images from "../utils/Images";

const SECTIONS = [
    {
        num: "1",
        title: "Who this app is for",
        body: (
            <p>
                XP StockFlow is intended for authorized staff of Team24 Retail
                Tech Private Limited and approved outlet users. It is not
                intended for children under 13 years of age (or the minimum
                age required in your jurisdiction), and we do not knowingly
                collect personal data from children.
            </p>
        ),
    },
    {
        num: "2",
        title: "Information we collect",
        body: (
            <>
                <p>
                    Depending on how you use the app, we may collect the
                    following categories of information:
                </p>

                <h3 className="mt-5 text-[14px] font-semibold text-[#08213D]">
                    2.1 Account and staff information
                </h3>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>Email address and password used for sign-in and authentication</li>
                    <li>Staff name, role or user type, outlet assignment, and permissions</li>
                    <li>Session details such as login status, last login time, and authentication tokens</li>
                </ul>

                <h3 className="mt-5 text-[14px] font-semibold text-[#08213D]">
                    2.2 Checklist and operational data
                </h3>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>Checklist answers, remarks, and completion status</li>
                    <li>Checklist category, type, frequency, and business date</li>
                    <li>Submission or draft status, timestamps, and related outlet or storage location</li>
                    <li>Draft content saved locally on your device so you can resume incomplete work</li>
                </ul>

                <h3 className="mt-5 text-[14px] font-semibold text-[#08213D]">
                    2.3 Location information
                </h3>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>Approximate or precise device location, including latitude and longitude</li>
                    <li>Location address or place name, when available</li>
                    <li>Location accuracy and related device permission status</li>
                </ul>
                <p className="mt-2">
                    Location may be collected when you submit or update a
                    checklist so we can verify that the activity took place at
                    the relevant outlet or operational location. If location
                    permission is denied, some checklist actions may not be
                    available.
                </p>

                <h3 className="mt-5 text-[14px] font-semibold text-[#08213D]">
                    2.4 Device and technical information
                </h3>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>Device type, browser, operating system, and app version</li>
                    <li>IP address and technical request data that may be logged by our hosting or authentication services</li>
                    <li>Local browser data such as saved session tokens, draft entries, and cached location details required for app functionality</li>
                </ul>
            </>
        ),
    },
    {
        num: "3",
        title: "How we use your information",
        body: (
            <>
                <p>We use the information for the following purposes:</p>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>To authenticate authorized staff and provide access to the app</li>
                    <li>To record, track, and review checklist submissions and operational compliance</li>
                    <li>To save drafts and help users continue incomplete checklist work</li>
                    <li>To verify submission location for operational integrity and audit purposes</li>
                    <li>To support app reliability, security, troubleshooting, and internal reporting</li>
                    <li>To comply with legal, regulatory, and audit requirements related to outlet operations</li>
                </ul>
            </>
        ),
    },
    {
        num: "4",
        title: "Legal basis and permissions",
        body: (
            <p>
                We process staff and operational data based on the necessity of
                providing workplace and outlet operations services, maintaining
                compliance, and supporting authorized use of the app. Location
                data is collected with your device permission when required for
                checklist submission or verification.
            </p>
        ),
    },
    {
        num: "5",
        title: "How we share information",
        body: (
            <>
                <p>We do not sell your personal information.</p>
                <p className="mt-3">We may share information only as needed with:</p>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>Authorized managers and administrators for operational review and compliance</li>
                    <li>Service providers that host, authenticate, or maintain the app and related infrastructure under confidentiality obligations</li>
                    <li>Regulators, auditors, or authorities when required by law or operational compliance requirements</li>
                </ul>
            </>
        ),
    },
    {
        num: "6",
        title: "Data retention",
        body: (
            <p>
                Checklist submissions, related staff identifiers, location data,
                and operational records are retained for as long as needed for
                compliance, audit, reporting, and legal purposes, or as
                otherwise required by company policy and applicable law. Drafts
                saved locally on your device may remain until you clear them,
                overwrite them, or sign out and remove the stored data.
            </p>
        ),
    },
    {
        num: "7",
        title: "Data security",
        body: (
            <p>
                We take reasonable technical and organizational measures to
                protect your information against unauthorized access, loss,
                misuse, or alteration. However, no method of transmission or
                storage is completely secure, and we cannot guarantee absolute
                security, especially for information stored locally on a device.
            </p>
        ),
    },
    {
        num: "8",
        title: "Your choices and rights",
        body: (
            <>
                <p>Depending on applicable law, you may have the right to:</p>
                <ul className="mt-2 list-disc space-y-1.5 pl-5">
                    <li>Request access to personal information we hold about you</li>
                    <li>Request correction of inaccurate information</li>
                    <li>Request deletion or restriction where legally permitted</li>
                    <li>Withdraw device location permission, which may affect checklist submission or verification</li>
                    <li>Clear saved app data or drafts from your browser or device settings</li>
                </ul>
                <p className="mt-3">
                    Because this app is used for workplace and outlet operations,
                    some requests may need to be handled through your outlet
                    manager or company administrator. Contact us using the
                    details below.
                </p>
            </>
        ),
    },
    {
        num: "9",
        title: "Children's privacy",
        body: (
            <p>
                XP StockFlow is not directed to children. We do not knowingly
                collect personal information from children. If you believe a
                child has provided personal information through the app,
                please contact us so we can take appropriate action.
            </p>
        ),
    },
    {
        num: "10",
        title: "Third-party services",
        body: (
            <p>
                The app may use trusted hosting, authentication, and support
                services to operate securely. These providers may process
                technical data such as IP address and device information in
                accordance with their own privacy practices. We do not
                intentionally share checklist or staff personal data with
                advertising networks.
            </p>
        ),
    },
    {
        num: "11",
        title: "International users",
        body: (
            <p>
                If you access the app from outside the country where our
                operations or hosting services are located, your information
                may be processed in another country as part of service
                delivery, support, and compliance operations.
            </p>
        ),
    },
    {
        num: "12",
        title: "Changes to this privacy policy",
        body: (
            <p>
                We may update this Privacy Policy from time to time. The
                &quot;Effective date&quot; at the top of this page will be
                revised when changes are made. Continued use of the app after
                an update means you acknowledge the revised Privacy Policy.
            </p>
        ),
    },
    {
        num: "13",
        title: "Contact us",
        body: (
            <>
                <p>
                    If you have questions about this Privacy Policy or your
                    information, contact:
                </p>
                <p className="mt-3">
                    Team24 Retail Tech Private Limited
                    <br />
                    Developer and publisher of the XP StockFlow application
                </p>
                <p className="mt-3">
                    Email: {" "}
                    <a
                        href="mailto:info@team24.in"
                        className="font-medium text-[#00A4E3] underline underline-offset-2 hover:text-[#04698F]"
                    >
                        info@team24.in
                    </a>
                </p>
            </>
        ),
    },
];

export default function PrivacyPolicy() {
    const navigate = useNavigate();

    return (
        <main className="min-h-screen bg-white xl-body">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@600;700&family=Inter:wght@400;500;600&display=swap');
                .xl-display { font-family: 'Space Grotesk', 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .xl-body { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .policy-body p, .policy-body li { line-height: 1.75; }
                .policy-body { color: #475569; font-size: 14.5px; }
                .policy-body strong, .policy-body em { color: #08213D; font-style: normal; font-weight: 600; }
            `}</style>

            <div className="mx-auto max-w-[720px] px-6 py-10 md:py-16">
                <button
                    type="button"
                    onClick={() => navigate('/profile')}
                    className="xl-body inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#5B6B7C] transition hover:text-[#00A4E3]"
                >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                    Back
                </button>

                <header className="mt-6 pb-8">
                    <div className="rounded-[28px] border border-[#E7EEF2] bg-[#F7FBFE] p-6 shadow-sm">
                        <div className="flex flex-col items-center gap-4 text-center">
                            <div className="flex h-20 w-20 items-center justify-center rounded-[24px] bg-white shadow-[0_20px_50px_rgba(4,105,143,0.14)]">
                                <img
                                    src={Images.logo1}
                                    alt="XP StockFlow logo"
                                    className="h-14 w-14 object-contain"
                                />
                            </div>
                            <div>
                                <h1 className="xl-display text-[30px] font-bold leading-tight text-[#08213D]">
                                    Privacy Policy
                                </h1>
                                <p className="mt-3 text-[14px] font-medium text-[#5B6B7C]">
                                    XP StockFlow · Effective date: 6 August 2026
                                </p>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="policy-body mt-8 space-y-4">
                    <p>
                        This Privacy Policy explains how{" "}
                        <strong>Team24 Retail Tech Private Limited</strong>{" "}
                        (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;), the developer
                        and publisher of the <strong>XP StockFlow</strong>
                        application, collects, uses, stores, and protects
                        information when authorized staff use the app.
                    </p>
                    <p>
                        Team24 Retail Tech Private Limited is the data
                        controller for the XP StockFlow application and is
                        responsible for the personal information collected
                        through the app.
                    </p>
                    <p>
                        XP StockFlow is an internal operations application used by
                        authorized staff to sign in, view stock-related tasks,
                        select storage locations, complete checklists, save
                        drafts, and submit operational records for review.
                    </p>
                    <p>
                        By using the XP StockFlow application, you agree to the
                        practices described in this Privacy Policy. If you do
                        not agree, please do not use the application.
                    </p>
                    <p>
                        We may use local storage and similar browser
                        technologies to save drafts, session data, and app
                        settings required for normal functionality.
                    </p>
                </div>

                <div className="mt-10 space-y-10">
                    {SECTIONS.map((section, index) => (
                        <div key={section.num}>
                            <h2 className="xl-display text-[17px] font-bold text-[#08213D]">
                                <span className="text-[#00A4E3]">{section.num}.</span>{" "}
                                {section.title}
                            </h2>

                            <div className="policy-body mt-3">{section.body}</div>

                            {index < SECTIONS.length - 1 && (
                                <div className="mt-10 border-t border-[#E7EEF2]" />
                            )}
                        </div>
                    ))}
                </div>

                <p className="policy-body mt-12 border-t border-[#E7EEF2] pt-8 text-[13px]">
                    This Privacy Policy applies to the XP StockFlow application
                    used by authorized staff.
                </p>
            </div>
        </main>
    );
}